import type { GameState } from '../core/state';
import { createInitialState } from '../core/state';
import type { EconomyConfig } from '../config/types';
import { now } from '../core/clock';

export const SAVE_KEY = 'burrow.save.v1';
export const BACKUP_KEY = 'burrow.save.backup';
/** Prefix for quarantined unreadable / newer-schema raw saves. Never overwritten by save(). */
export const QUARANTINE_PREFIX = 'burrow.save.quarantine.';

export type SaveStorage = {
  get: (key: string) => Promise<string | null>;
  set: (key: string, value: string) => Promise<void>;
  remove?: (key: string) => Promise<void>;
};

/** localStorage adapter (browser / vitest). */
export function createLocalStorageAdapter(
  store: Storage = globalThis.localStorage,
): SaveStorage {
  return {
    get: async (key) => store.getItem(key),
    set: async (key, value) => {
      store.setItem(key, value);
    },
    remove: async (key) => {
      store.removeItem(key);
    },
  };
}

/** In-memory adapter for unit tests. */
export function createMemoryStorage(initial: Record<string, string> = {}): SaveStorage {
  const map = new Map<string, string>(Object.entries(initial));
  return {
    get: async (key) => map.get(key) ?? null,
    set: async (key, value) => {
      map.set(key, value);
    },
    remove: async (key) => {
      map.delete(key);
    },
  };
}

/** Capacitor Preferences adapter. Dynamic import so node unit tests never load native. */
export async function createPreferencesAdapter(): Promise<SaveStorage> {
  const { Preferences } = await import('@capacitor/preferences');
  return {
    get: async (key) => {
      const { value } = await Preferences.get({ key });
      return value;
    },
    set: async (key, value) => {
      await Preferences.set({ key, value });
    },
    remove: async (key) => {
      await Preferences.remove({ key });
    },
  };
}

export type MigrationFn = (raw: Record<string, unknown>) => Record<string, unknown>;

/**
 * Schema migrations map: version N → transform to N+1.
 * Never delete entries — old saves must keep migrating.
 */
export const migrations: Record<number, MigrationFn> = {
  // v1 is current; placeholder for future:
  // 1: (raw) => { raw.schemaVersion = 2; return raw; },
};

function parseJson(text: string): unknown {
  return JSON.parse(text) as unknown;
}

function isGameStateShape(raw: unknown): raw is Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return false;
  const o = raw as Record<string, unknown>;
  return (
    typeof o.schemaVersion === 'number' &&
    typeof o.currencies === 'object' &&
    o.currencies !== null &&
    typeof o.site === 'object' &&
    o.site !== null &&
    typeof o.hero === 'object' &&
    o.hero !== null &&
    typeof o.rngSeed === 'number'
  );
}

function migrate(
  raw: Record<string, unknown>,
  targetVersion: number,
): Record<string, unknown> {
  let version = raw.schemaVersion as number;
  let cur = raw;
  while (version < targetVersion) {
    const fn = migrations[version];
    if (!fn) {
      throw new Error(
        `save migration missing for schemaVersion ${version} → ${version + 1}`,
      );
    }
    const from = version;
    cur = fn(structuredClone(cur));
    version = cur.schemaVersion as number;
    if (version <= from) {
      throw new Error(`save migration did not advance version from ${from}`);
    }
  }
  return cur;
}

export type LoadSource = 'main' | 'backup' | 'fresh' | 'blocked';

export interface LoadResult {
  /** Null only when source === 'blocked' (newer schema — do not boot / autosave). */
  state: GameState | null;
  source: LoadSource;
  migrated: boolean;
  /** Quarantine keys written this load (raw text preserved; save() never touches them). */
  quarantineKeys: string[];
  /** Present when source === 'blocked'. */
  blockedReason?: 'newer_schema';
  foundSchemaVersion?: number;
}

export interface SaveService {
  save: (state: GameState) => Promise<void>;
  load: () => Promise<LoadResult>;
  exportBase64: (state: GameState) => string;
  clearForTests?: () => Promise<void>;
}

type TryLoadOk = { ok: true; state: GameState; migrated: boolean };
type TryLoadFail =
  | { ok: false; kind: 'missing' }
  | {
      ok: false;
      kind: 'corrupt' | 'migrate_fail' | 'newer_schema';
      raw: string;
      schemaVersion?: number;
    };
type TryLoadResult = TryLoadOk | TryLoadFail;

export function createSaveService(
  economy: EconomyConfig,
  storage: SaveStorage,
): SaveService {
  const targetVersion = economy.save.schemaVersion;

  async function writeBoth(serialized: string): Promise<void> {
    // Promote current main → backup, then write main.
    // Never touches quarantine keys.
    const prev = await storage.get(SAVE_KEY);
    if (prev) {
      await storage.set(BACKUP_KEY, prev);
    }
    await storage.set(SAVE_KEY, serialized);
  }

  let quarantineSeq = 0;
  async function quarantine(raw: string, reason: string): Promise<string> {
    const key = `${QUARANTINE_PREFIX}${now()}.${quarantineSeq++}`;
    await storage.set(key, raw);
    console.error(
      `[burrow.save] quarantined unreadable/newer save (${reason}) → ${key}`,
    );
    return key;
  }

  async function tryLoadKey(key: string): Promise<TryLoadResult> {
    const text = await storage.get(key);
    if (!text) return { ok: false, kind: 'missing' };
    let parsed: unknown;
    try {
      parsed = parseJson(text);
    } catch {
      return { ok: false, kind: 'corrupt', raw: text };
    }
    if (!isGameStateShape(parsed)) {
      return { ok: false, kind: 'corrupt', raw: text };
    }
    let migrated = false;
    let raw = parsed;
    const version = raw.schemaVersion as number;
    try {
      if (version < targetVersion) {
        raw = migrate(raw, targetVersion);
        migrated = true;
      } else if (version > targetVersion) {
        return {
          ok: false,
          kind: 'newer_schema',
          raw: text,
          schemaVersion: version,
        };
      }
    } catch {
      return { ok: false, kind: 'migrate_fail', raw: text };
    }
    return { state: raw as unknown as GameState, migrated, ok: true };
  }

  return {
    async save(state: GameState) {
      // lastSavedAt never moves backwards (clock-rollback exploit).
      const t = now();
      state.lastSavedAt = Math.max(state.lastSavedAt ?? 0, t);
      state.schemaVersion = targetVersion;
      await writeBoth(JSON.stringify(state));
    },

    async load() {
      const quarantineKeys: string[] = [];
      const main = await tryLoadKey(SAVE_KEY);
      if (main.ok) {
        return {
          state: main.state,
          source: 'main' as const,
          migrated: main.migrated,
          quarantineKeys,
        };
      }

      if (main.kind === 'newer_schema') {
        const q = await quarantine(main.raw, 'newer_schema:main');
        quarantineKeys.push(q);
        console.error(
          `[burrow.save] save schemaVersion ${main.schemaVersion} > app ${targetVersion}; blocking boot (please update the app)`,
        );
        return {
          state: null,
          source: 'blocked' as const,
          migrated: false,
          quarantineKeys,
          blockedReason: 'newer_schema' as const,
          foundSchemaVersion: main.schemaVersion,
        };
      }

      const backup = await tryLoadKey(BACKUP_KEY);
      if (backup.ok) {
        await storage.set(SAVE_KEY, JSON.stringify(backup.state));
        // Quarantine unreadable main if we had one
        if (main.kind === 'corrupt' || main.kind === 'migrate_fail') {
          quarantineKeys.push(await quarantine(main.raw, `main:${main.kind}`));
        }
        return {
          state: backup.state,
          source: 'backup' as const,
          migrated: backup.migrated,
          quarantineKeys,
        };
      }

      if (backup.kind === 'newer_schema') {
        if (main.kind === 'corrupt' || main.kind === 'migrate_fail') {
          quarantineKeys.push(await quarantine(main.raw, `main:${main.kind}`));
        }
        quarantineKeys.push(
          await quarantine(backup.raw, 'newer_schema:backup'),
        );
        console.error(
          `[burrow.save] backup schemaVersion ${backup.schemaVersion} > app ${targetVersion}; blocking boot (please update the app)`,
        );
        return {
          state: null,
          source: 'blocked' as const,
          migrated: false,
          quarantineKeys,
          blockedReason: 'newer_schema' as const,
          foundSchemaVersion: backup.schemaVersion,
        };
      }

      // Both unreadable / missing → fresh, but quarantine any raw we still have.
      if (main.kind === 'corrupt' || main.kind === 'migrate_fail') {
        quarantineKeys.push(await quarantine(main.raw, `main:${main.kind}`));
      }
      if (backup.kind === 'corrupt' || backup.kind === 'migrate_fail') {
        quarantineKeys.push(
          await quarantine(backup.raw, `backup:${backup.kind}`),
        );
      }

      return {
        state: createInitialState(economy),
        source: 'fresh' as const,
        migrated: false,
        quarantineKeys,
      };
    },

    exportBase64(state: GameState) {
      const json = JSON.stringify(state);
      if (typeof btoa === 'function') {
        return btoa(json);
      }
      return Buffer.from(json, 'utf8').toString('base64');
    },

    async clearForTests() {
      await storage.remove?.(SAVE_KEY);
      await storage.remove?.(BACKUP_KEY);
    },
  };
}
